<template>
  <DamageMapSkeleton>
    <template #damageMap>
      <div class="damage-map_wrap">
        <div class="damage-map--img">
          <img
            :alt="item?.characteristics?.side?.toString()"
            :height="921"
            :src="getImageFromProxy(image, '528:921')"
            :width="528"
            loading="lazy"
          >
        </div>

        <div class="damage-map--hits">
          <div class="damage-map--hits_item head">
            <div class="amount">
              <p class="lighten">
                {{ damages.head.notArmored }}
              </p>
              <span>/</span>
              <p class="green">
                {{ damages.head.armored }}
              </p>
            </div>
            <div class="dot" />
          </div>
          <div class="damage-map--hits_item chest">
            <div class="amount">
              <p class="lighten">
                {{ damages.arms.notArmored }}
              </p>
              <span>/</span>
              <p class="green">
                {{ damages.arms.armored }}
              </p>
            </div>
            <div class="dot" />
          </div>
          <div class="damage-map--hits_item pelvis">
            <div class="amount">
              <p class="lighten">
                {{ damages.body.notArmored }}
              </p>
              <span>/</span>
              <p class="green">
                {{ damages.body.armored }}
              </p>
            </div>
            <div class="dot" />
          </div>
          <div class="damage-map--hits_item leg">
            <div class="amount">
              <p class="lighten">
                {{ damages.legs.notArmored }}
              </p>
              <span>/</span>
              <p class="green">
                {{ damages.legs.armored }}
              </p>
            </div>
            <div class="dot" />
          </div>
        </div>
      </div>
    </template>
    <template #damageRange>
      <div class="damage-range_option mb12">
        <div class="option_item f fs gap8 light">
          <span class="light" />
          <p>{{ $t('no_armor') }}</p>
        </div>
        <div class="option_item f fs gap8 green">
          <span class="blue_light" />
          <p>{{ $t('kevlar_helmet') }}</p>
        </div>
      </div>
      <div class="damage-range_slider gap13">
        <p class="f13 fw300 mb8">
          {{ $t('distance') }}
        </p>
        <div class="f gap12 f12 light">
          <span class="mb2">{{ $t('range', { n: 1 }) }}</span>
          <RangeSlider v-model="distance" />
          <span class="mb2">{{ $t('range', { n: 100 }) }}</span>
        </div>
      </div>
    </template>
  </DamageMapSkeleton>
</template>

<script lang="ts" setup>
import type { WeapoData } from '~/types/WeapoData';
import DamageMapSkeleton from '~/components/Pages/Weapon/Components/DamageMapSkeleton.vue';
import RangeSlider from '~/components/ui/RangeSlider.vue';
import { ranges } from '~/data/ranges';

const props = defineProps<{
  item: WeapoData
  name: string
}>();

const distance = ref<number>(1);
const cachedImage = useState('cachedImage', () => '');

const image = computed(() => {
  if (props.item) {
    return `https://waxpeer.com/img/agents/${cachedImage.value}.png`;
  } else {
    return '';
  }
});

watchEffect(() => {
  if (props.item && !cachedImage.value) {
    cachedImage.value = getImage(props.item?.characteristics?.side || '');
  }
});

const rangeCoefficient = computed(() => {
  const name = props.name.replace('_', '-');
  return ranges.find((item) => {
    return item.name === name;
  })?.range || 0.85;
});

function getRandomInt(max: number) {
  return Math.floor(Math.random() * max);
}

function getImage(side: string) {
  if (side === 'Terrorists') {
    return `ct_${(getRandomInt(31) + 1).toString()}`;
  }
  if (side === 'Both') {
    return (getRandomInt(2) === 0 ? 'ct_' : 't_') + (getRandomInt(26) + 1).toString();
  }

  return `t_${(getRandomInt(26) + 1).toString()}`;
}

function calculateDamage(hitArea: 'body' | 'head' | 'legs' | 'arms', armor: boolean): number {
  const characteristics = props.item?.characteristics;
  let baseDamage: number;

  switch (hitArea) {
    case 'head': {
      const [unarmoredHeadDamage, armoredHeadDamage] = characteristics?.head_damage?.split('/').map(Number) || [];
      baseDamage = armor ? armoredHeadDamage : unarmoredHeadDamage;
      break;
    }
    case 'body': {
      const [unarmoredBodyDamage, armoredBodyDamage] = characteristics?.body_damage?.split('/').map(Number) || [];
      baseDamage = armor ? armoredBodyDamage : unarmoredBodyDamage;
      break;
    }
    case 'legs': {
      const [unarmoredLegsDamage] = characteristics?.body_damage?.split('/').map(Number) || [];
      baseDamage = unarmoredLegsDamage * 0.75; // Уменьшение урона для ног
      break;
    }
    case 'arms': {
      const [unarmoredArmsDamage, armoredArmsDamage] = characteristics?.body_damage?.split('/').map(Number) || [];
      baseDamage = (armor ? armoredArmsDamage : unarmoredArmsDamage) * 0.85; // Уменьшение урона для рук
      break;
    }
    default:
      throw new Error('Invalid hit area');
  }

  const distanceLocal = (distance.value / 0.0254).toFixed(2);
  const finalDamageMultiplier = rangeCoefficient.value ** (+distanceLocal / 500);

  return +(baseDamage * finalDamageMultiplier).toFixed();
}

const damages = computed(() => {
  return {
    head: {
      armored: calculateDamage('head', true),
      notArmored: calculateDamage('head', false),
    },
    body: {
      armored: calculateDamage('body', true),
      notArmored: calculateDamage('body', false),
    },
    legs: {
      armored: calculateDamage('legs', true),
      notArmored: calculateDamage('legs', false),
    },
    arms: {
      armored: calculateDamage('arms', true),
      notArmored: calculateDamage('arms', false),
    },
  };
});
</script>

<style lang="scss">
@use '@/assets/scss/variables.scss' as *;
@use './_vars.scss' as *;

.damage {
  &-map {
    width: 100%;

    &_wrap {
      position: relative;
      width: 26.3rem;
      max-width: 100%;

      @media screen and (max-width: 979px) {
        margin-top: -2rem;
      }
    }

    &--img {
      position: relative;
      z-index: 3;
      margin-top: -$damage-map-negative-margin-top;
      margin-bottom: -$damage-map-negative-margin-bottom;

      img {
        display: block;
        height: $damage-map-image-height;
        object-fit: cover;
        position: relative;
        z-index: 1;
        filter: drop-shadow(0 2px 10px rgba(0, 0, 0, 0.1));
      }

      &:after {
        content: '';
        display: block;
        position: absolute;
        bottom: 2.5rem;
        left: 50%;
        transform: translateX(-50%);
        border-radius: 100%;
        width: 70%;
        height: 2rem;
        background: #000;
        filter: blur(1.2rem);
        opacity: 0.5;
      }
    }

    &--hits {
      &_item {
        font-size: 1.4rem;
        font-weight: 300;
        color: var(--gray);
        position: absolute;
        top: 0;
        z-index: 10;

        .amount {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.4rem;
          width: 6.5rem;
          height: 2.6rem;
          border-bottom: 1px solid #252930;
          border-bottom: 1px solid var(--gray);
        }

        .dot {
          width: 5px;
          height: 5px;
          border-radius: 6px;
          background: var(--accent-primary);
          position: absolute;
          color: inherit;
        }

        &:before {
          content: '';
          display: block;
          background: currentColor;
          position: absolute;
        }
      }

      .head {
        top: 1.5rem;
        left: 64%;

        .dot {
          bottom: -2px;
          left: 0;
        }
      }

      .chest {
        top: 10rem;
        left: -0.6rem;

        .dot {
          bottom: -2px;
          right: 0;
        }
      }

      .pelvis {
        right: -0.7rem;
        top: 7rem;

        &:before {
          width: 8rem;
          height: 1px;
          transform: rotate(113deg);
          transform-origin: bottom left;
          top: 2.5rem;
          left: 0;
          z-index: 10;
        }

        .dot {
          top: 10rem;
          left: -3.5rem;
        }
      }

      .leg {
        top: 27rem;
        left: 1.6rem;

        .dot {
          bottom: -2px;
          right: 0;
        }
      }
    }
  }

  &-range {
    &_slider {
      // height: 4rem;
      background: var(--surfaces-high-container);
      border-radius: .8rem;
      padding: 1.2rem 1.6rem;

      .range-slider {
        width: 100%;
        display: flex;
        padding: 0;

        &:hover,
        &:active,
        &:focus {
          .damage-range_tip {
            opacity: 1;
            visibility: visible;
          }
        }
      }

      .range-slider-rail {
        height: 0.2rem;
        background: var(--gray);
      }

      .range-slider-fill {
        height: 0.2rem;
        background: linear-gradient(
                270deg,
                #53a7ed -0.6%,
                rgba(83, 167, 237, 0) 109.44%
        );
      }

      .range-slider-knob {
        background: var(--accent-primary);
        width: 14px;
        height: 14px;
        border: none;
        z-index: 6;
        box-shadow: 0 0 0 2px color-mix(in srgb, var(--accent-primary) 50%, transparent);
        position: relative;

        &:hover,
        &:active,
        &:focus {
          .damage-range_tip {
            opacity: 1;
            visibility: visible;
          }
        }
      }
    }

    &_tip {
      position: absolute;
      bottom: -3.7rem;
      left: 50%;
      transform: translateX(-50%);

      font-weight: 400;
      font-size: 1.2rem;
      background: var(--surfaces-high-container);
      box-shadow: 0 0 1rem rgba(26, 26, 26, 0.5);
      border-radius: .8rem;
      height: 3rem;
      padding: 0 1.2rem;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      visibility: hidden;
      transition: 0.3s;
    }

    &_option {
      .option_item {
        font-weight: 300;
        font-size: 1.3rem;
        line-height: 2.2rem;

        span {
          width: 1.4rem;
          height: 1.4rem;
          border-radius: 50%;

          &.light {
            background: var(--surfaces-on-surface-variable);
          }

          &.blue_light {
            background: var(--accent-primary);
          }
        }
      }
    }
  }

  &-char {
    &_item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-size: 1.4rem;
      font-weight: 400;
    }
  }
}

@media screen and (max-width: 600px) {
  .damage {
    display: block;

    &_info {
      display: flex;
      flex-wrap: wrap;
    }

    &-char {
      order: 1;
    }

    &-range {
      &_option {
        display: flex;
        gap: 1.6rem;
        flex-wrap: wrap;
      }
    }
  }
}
</style>
